import React, { useEffect } from "react";

import { useClientOnly } from "@/util/ClientOnly.jsx";
import { ModuleImportError } from "@/util/custom-errors.mjs";

/**
 * Convenience function to wrap a dynamically imported component using
 * the file's default export. The import must be passed in as
 * `() => import("...")` so that static analysis works.
 */
export function lazyComponent<Fn extends ImportComponentFn>(
  importFn: Fn,
): React.FC<LazyComponentProps<LazyComponentFnProps<Fn>>> {
  let loadComponent: Promise<React.FC<LazyComponentFnProps<Fn>>>;

  return function LazyComponent({
    props,
    fallback,
    forceFallback,
    error,
  }: LazyComponentProps<LazyComponentFnProps<Fn>>) {
    const isHydrated = useClientOnly();

    const [Component, setComponent] = React.useState<React.FC<
      LazyComponentFnProps<Fn>
    > | null>(null);

    const [importError, setImportError] = React.useState<unknown | null>(null);

    useEffect(() => {
      if (!isHydrated) return;

      if (loadComponent === undefined) {
        loadComponent = importFn().then((module) => module.default);
      }

      let isMounted = true;

      loadComponent
        .then((component) => {
          if (isMounted) {
            setComponent(() => component);
            setImportError(null);
          }
        })
        .catch((err: unknown) => {
          if (isMounted) {
            setImportError(err);
            throw new ModuleImportError(err["message"]);
          }
        });

      return () => {
        isMounted = false;
      };
    }, [isHydrated]);

    if (!isHydrated) {
      return fallback;
    }

    if (importError !== null) {
      return error ?? null;
    }

    if (Component === null || forceFallback === true) {
      return fallback;
    }

    return React.createElement(Component, props);
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
type ImportComponentFn<P = {}> = () => Promise<{
  default: React.FC<P>;
}>;

type LazyComponentFnProps<Fn extends ImportComponentFn> =
  Fn extends ImportComponentFn<infer Props>
    ? IsUnknown<Props> extends true
      ? // eslint-disable-next-line @typescript-eslint/ban-types
        {}
      : Props
    : never;

// eslint-disable-next-line @typescript-eslint/ban-types
type LazyComponentProps<P = {}> = Unwrap<
  {
    fallback: React.ReactNode;
    /**
     * Force fallback to be rendered. Useful during development.
     */
    forceFallback?: boolean;
    error?: React.ReactNode;
  } & (NonNullable<unknown> extends P
    ? { props?: P }
    : // If any prop is required, then we require `props` to be passed
      { props: P })
>;
